import { contactTailwindTypesColors, enumToHuman } from '@/shared';
import {
    type BankTransaction,
    type ICompany,
    type IContactPerson,
    type IInvoice,
    type InvestorInfo,
    type Rent,
    type RentTenancy,
    type RentingLead,
    RentingLeadStatus,
    getContactDisplayName,
    getContactShortName,
    getPartyShortName,
} from '@condo/domain';
import { intlDateFormat } from '@condo/formatters';
import dayjs from 'dayjs';
import type { SelectOption } from 'naive-ui';
import { h } from 'vue';

export const renderInvLabel = (option: SelectOption) => {
    const i = option.i as InvestorInfo;
    if (!i) {
        return h('span', { class: 'text-gray-600 font-semibold font-mono' }, { default: () => option.label });
    }
    const investors = i.investors?.map(inv => getPartyShortName(inv)).join(', ') ?? '';
    return [
        h('span', { class: 'text-gray-600 font-semibold font-mono' }, { default: () => `ID:${i.investorInfoId}, ` }),
        h('span', { class: 'text-sky-700 font-semibold font-mono' }, { default: () => `${investors}, ` }),
        h(
            'span',
            { class: 'text-green-700 font-mono font-semibold' },
            { default: () => `${intlDateFormat(i.startDate)} - ${i.endDate ? intlDateFormat(i.endDate) : '∞'}` },
        ),
    ];
};
export const renderRentLabel = (option: SelectOption & { rent: Rent }) => {
    const { rent } = option;
    // const tenants = rt.tenancyMembers?.map(tenant => getPartyShortName(tenant)).join(', ') ?? '';
    if (!rent) {
        return h('span', { class: 'text-gray-600 font-semibold font-mono' }, { default: () => option.label });
    }
    return [
        h('span', { class: 'text-sky-700 font-semibold font-mono' }, { default: () => `${tenants}, ` }),
        h('span', { class: 'text-gray-600 font-semibold font-mono' }, { default: () => `ID:${rent.rentId}, ` }),
        h(
            'span',
            { class: `font-semibold ${rent.isActive ? 'text-green-700' : 'text-gray-500'} font-mono` },
            { default: () => `${intlDateFormat(rent.startDate)} - ${rent.endDate ? intlDateFormat(rent.endDate) : '∞'}` },
        ),
    ];
};
export const renderTenancyLabel = (option: SelectOption) => {
    const rt = option.rt as RentTenancy;
    const tenants = rt?.tenancyMembers?.map(tenant => getPartyShortName(tenant)).join(', ') ?? '';
    if (!rt) {
        return h('span', { class: 'text-red-600 font-semibold font-mono' }, { default: () => option.label });
    }
    return [
        h('span', { class: 'text-gray-600 font-semibold font-mono' }, { default: () => `ID:${rt.rentTenancyId}, ` }),
        h('span', { class: 'text-sky-700 font-semibold font-mono' }, { default: () => `${tenants}, ` }),
        h(
            'span',
            { class: `font-semibold ${rt.isActive ? 'text-green-700' : 'text-gray-500'} font-mono` },
            { default: () => `${intlDateFormat(rt.startDate)} - ${rt.endDate ? intlDateFormat(rt.endDate) : '∞'}` },
        ),
    ];
};
export const renderRentingLeadLabel = (option: SelectOption) => {
    const rl = option.rentingLead as RentingLead;
    const names =
        (rl?.contacts?.map(contact => getContactDisplayName(contact)).join(', ') || rl?.companies?.map(company => company.name).join(', ')) ??
        'No contacts/companies found';
    if (!rl) {
        return h('span', { class: 'text-gray-600 font-semibold font-mono' }, { default: () => option.label });
    }
    // grayed out if not approved
    return [
        h(
            'span',
            { class: rl.rentingLeadStatus !== RentingLeadStatus.Approved ? 'text-gray-400' : 'text-gray-600 font-semibold font-mono' },
            {
                default: () =>
                    `${rl.rentingLeadStatus !== RentingLeadStatus.Approved ? '(Lead not approved yet) ' : ''} ${rl.rentingLeadId}: ${names || '(No contacts/companies found)'}`,
            },
        ),
    ];
};

export const renderBKALabel = (option: SelectOption & { rt: RentTenancy }) => {
    const { rt } = option;
    const tenants = rt.tenancyMembers?.map(tenant => getPartyShortName(tenant)).join(', ') ?? '';
    if (!rt) {
        return h('span', { class: 'text-gray-600 font-semibold font-mono' }, { default: () => option.label });
    }
    return [
        h('span', { class: 'text-gray-600 font-semibold font-mono' }, { default: () => `ID:${rt.rentTenancyId}, ` }),
        h('span', { class: 'text-sky-700 font-semibold font-mono' }, { default: () => `${tenants}, ` }),
        h(
            'span',
            { class: `font-semibold ${rt.isActive ? 'text-green-700' : 'text-gray-500'} font-mono` },
            { default: () => `${intlDateFormat(rt.startDate)} - ${rt.endDate ? intlDateFormat(rt.endDate) : '∞'}` },
        ),
    ];
};

export const renderTrxLabel = (option: SelectOption) => {
    const t = option.t as BankTransaction;
    if (!t) {
        return option.label;
    }
    return [
        h('span', { class: 'text-sky-700 font-semibold font-mono' }, { default: () => `${t.bankTransactionId}` }),
        h('span', { class: 'text-sky-700 font-mono' }, { default: () => `, ${formatDate(t.valueDate)}, ${t.categoryType}:` }),
        h(
            'span',
            { class: `font-semibold ${t.isCredit ? 'text-green-700' : 'text-red-600'}` },
            { default: () => ` ${t.isCredit ? '' : '-'}${formatAmount(t.amount)}` },
        ),
    ];
};

export const renderCompanyLabel = (option: SelectOption & { company: ICompany }) => {
    const c = option.company;
    if (!c) {
        return option.label;
    }
    return [
        h('span', { class: 'text-sky-700 font-semibold font-mono' }, { default: () => `${c.shortName ?? c.name}` }),
        h('span', { class: 'text-gray-600 font-mono' }, { default: () => `, ${enumToHuman(c.type)} ${c.isCondoCompany ? '(Condo)' : ''}` }),
    ];
};

export const renderInvoiceLabel = (option: SelectOption & { invoice: IInvoice }) => {
    const { invoice } = option;
    if (!invoice) {
        return h('span', { class: 'text-gray-600 font-semibold font-mono' }, { default: () => option.label });
    }
    const condoSide = invoice?.creditorAccounting?.company?.isCondoCompany
        ? invoice?.creditorAccounting?.company?.shortName
        : invoice?.debtorAccounting?.company?.isCondoCompany
          ? invoice?.debtorAccounting?.company?.shortName
          : undefined;

    return [
        h('span', { class: 'text-gray-600/75 font-semibold font-mono' }, { default: () => `ID:${invoice.invoiceId}, ` }),
        h('span', { class: 'text-gray-700 font-semibold font-mono' }, { default: () => `${formatAmount(invoice.amount!)}, ` }),
        h('span', { class: `font-semibold text-gray-500 font-mono` }, { default: () => `${intlDateFormat(invoice.receiptDate!)}` }),
        h(
            'span',
            {
                class: 'inline-flex shrink-0 items-center rounded-full bg-sky-600 px-1.5 py-0.5 text-xs font-semibold text-white ring-1 ring-green-600/20 ring-inset ml-2',
            },
            { default: () => `${condoSide}` },
        ),
        h(
            'span',
            {
                class: 'inline-flex shrink-0 items-center rounded-full bg-sky-700 px-1.5 py-0.5 text-xs font-semibold text-white ring-1 ring-green-600/20 ring-inset ml-2',
            },
            { default: () => `${enumToHuman(invoice.costType)}` },
        ),
        h(
            'span',
            {
                class: 'inline-flex shrink-0 items-center rounded-full bg-green-50 px-1.5 py-0.5 text-xs font-semibold text-green-700 ring-1 ring-green-600/20 ring-inset ml-2',
            },
            { default: () => `${enumToHuman(invoice.state)}` },
        ),
    ];
};

export const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
};
export const formatDate = (date: Date, format = 'DD.MM.YYYY') => {
    return dayjs(date).format(format);
};
export const renderContactLabel = (option: SelectOption & { contact: IContactPerson; disabled: boolean }) => {
    const c = option.contact;
    if (!c) {
        return option.label;
    }
    return [
        h(
            'span',
            {
                class: `inline-flex shrink-0 items-center rounded-full ${option.disabled ? 'bg-gray-200' : contactTailwindTypesColors[c.contactType!]} px-1.5 py-0.5 text-xs font-semibold text-white ring-1 ring-green-600/20 ring-inset mr-2`,
            },
            { default: () => `${enumToHuman(c.contactType)}` },
        ),
        h(
            'span',
            { class: `${option.disabled ? 'text-gray-500/65' : 'text-sky-700'} font-semibold font-mono` },
            { default: () => `${getContactShortName(c)}` },
        ),
        h(
            'span',
            { class: `${option.disabled ? 'text-gray-500/65' : 'text-gray-600'} font-mono` },
            { default: () => ` (${c.email?.length ? c.email : 'No Email'})` },
        ),
    ];
};

export const renderContactOrCompanyLabel = (option: SelectOption & { contactOrCompany: IContactPerson | ICompany; disabled: boolean }) => {
    const c = option.contactOrCompany;
    if (!c) {
        return option.label;
    }
    const type = c?.contactId ? c?.contactType : c?.type;
    const name = c?.contactId ? getContactShortName(c as IContactPerson) : c?.name;
    return [
        h(
            'span',
            {
                class: `inline-flex shrink-0 items-center rounded-full ${option.disabled ? 'bg-gray-200' : contactTailwindTypesColors[type]} px-1.5 py-0.5 text-xs font-semibold text-white ring-1 ring-green-600/20 ring-inset mr-2`,
            },
            { default: () => `${enumToHuman(type)}` },
        ),
        h('span', { class: `${option.disabled ? 'text-gray-500/65' : 'text-sky-700'} font-semibold font-mono` }, { default: () => `${name}` }),
        h(
            'span',
            { class: `${option.disabled ? 'text-gray-500/65' : 'text-gray-600'} font-mono` },
            { default: () => ` (${c.email?.length ? c.email : 'No Email'})` },
        ),
    ];
};
